import { DressupClothing } from "GameObjects/StoryGame/ch1/DressupClothing";
import { DressupHolly } from "GameObjects/StoryGame/ch1/DressupHolly";
import { SceneContainer } from "GameObjects/StoryGame/SceneContainer";
import { SceneDraggable } from "GameObjects/StoryGame/SceneDraggable";
import { SceneSpine } from "GameObjects/StoryGame/SceneSpine";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Elastic, TimelineLite, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { changeParent } from "Utils/layoutUtils";
import { list, Mix } from "Utils/Reflection";
import * as Topics from "Data/Topics";

import {ch1_p07} from "../../../layouts/ch1_p07/ch1_p07";

export class Chapter1Page7 extends AbstractStoryState {

	readonly background = this.addChild(new SceneSprite());

	readonly skirt = this.addChild(new DressupClothing({clickSFX: "HollySkirt"}));
	readonly skirtHangerEmpty = this.addChild(new SceneSprite());
	readonly necklace = this.addChild(new DressupClothing({clickSFX: "HollyNecklaceOpt1"}));
	readonly necklacePoleCover = this.addChild(new SceneSprite());
	readonly jacketHangerEmpty = this.addChild(new SceneSprite());
	readonly jacketHanger = this.addChild(new SceneSprite());
	readonly jacket = this.addChild(new DressupClothing({clickSFX: "HollyJacket"}));
	readonly boots = this.addChild(new DressupClothing({clickSFX: "HollyBootsOpt2"}));

	readonly frameRight = this.addChild(new SceneSprite());
	readonly frameLeft = this.addChild(new SceneSprite());
	readonly lines = this.addChild(new SceneSprite());

	readonly holly = this.addChild(new DressupHolly());

    public init(pSize: PIXI.Point, pData: any) {
		// exported layout from Animate CC
		const i = ch1_p07.call(this);

		// super.init calls onResize
		super.init(pSize, pData);

		// initial state and object nesting
		this.skirt.interactive = false;
		this.jacket.interactive = false;
		this.boots.interactive = false;
		this.necklace.interactive = false;
		this.jacketHangerEmpty.visible = false;
		changeParent(this.skirtHangerEmpty, this.skirt.visuals);
		changeParent(this.jacketHanger, this.jacket.visuals);
		changeParent(this.necklacePoleCover, this.necklace);

		this._subscriptions.push([
			// callbacks from holly
			// TODO: constants instead of strings
			PubSub.subscribe('wear', this.onItemDropped.bind(this)),
			PubSub.subscribe('wearing', this.onOutfitUpdate.bind(this)),
			// callbacks from clothing
			PubSub.subscribe(HLF.DRAG_BEGIN, this.onDragBegin.bind(this)),
			PubSub.subscribe(HLF.DRAGGABLE_SELECTED, this.onDragBegin.bind(this)),
			PubSub.subscribe(HLF.DRAG_END, this.onDragEnd.bind(this)),
			PubSub.subscribe(HLF.DRAGGABLE_DESELECTED, this.onDragEnd.bind(this)),
		]);
	}

	public animateIn(pOnComplete: ()=>void) {
		this.holly.update(0);
		super.animateIn(pOnComplete);
		this.skirt.Enable();
		this.showHint(this.skirt);
	}

	public update(pDeltaTime: number) {
		super.update(pDeltaTime);
		this.holly.update(pDeltaTime);
	}

	// called *before* onDragEnd
	protected onItemDropped(pTopic: string, pData: HLF.Input.Draggable): void {
		if (pData instanceof DressupClothing) {
			PubSub.publishSync(Topics.HIDE_ALL_HINTS, undefined);
			if (pData === this.jacket) {
				changeParent(this.jacketHangerEmpty, this);
				this.jacketHangerEmpty.visible = true;
			}
			else if (pData === this.skirt) {
				changeParent(this.skirtHangerEmpty, this);
				this.skirtHangerEmpty.visible = true;
			}
			pData.Wear();
		}
	}

	protected onOutfitUpdate(pTopic: string, pData: {[key: string]: boolean}): void {
		if (pData.skirt) {
			if (!pData.jacket) {
				this.jacket.Enable();
				this.showHint(this.jacket);
			}
			else {
				if (pData.necklace === false) {
					this.necklace.Enable();
					this.showHint(this.necklace);
				}
				else if (pData.boots === false) {
					this.boots.Enable();
					this.showHint(this.boots);
				}
			}
		}

        // if all clothing layers are on
        if (!Object.values(pData).some(c=>!c)) {
			PubSub.publishSync(Topics.HIDE_ALL_HINTS, undefined);
			new TimelineLite().call(()=>{
				this.gotoNextState({transition: "up"});
			}).delay(1);
		}
	}

	// called *after* onItemDropped
	protected onDragEnd(pTopic: string, pData: HLF.Input.Draggable): void {
		// if drag ended without dropping on holly, or item deselected
		if (pData instanceof DressupClothing && !pData.worn && pData.interactive) {
			// reset hanger position and scale
			pData.visuals.position.set(0);
			pData.visuals.scale.set(1);

			if (pData === this.skirt) {
				// re-parent the skirt hanger
				changeParent(this.skirtHangerEmpty, this.skirt.visuals);
			}
			else if(pData === this.jacket) {
				// hide the empty hanger
				this.jacketHangerEmpty.visible = false;
				this.jacketHanger.visible = true;
			}
			else if (pData === this.necklace) {
				// show the patch
				this.necklacePoleCover.visible = true;
			}

			// re-hint the item
			this.showHint(pData);
		}
	}

	protected onDragBegin(pTopic: string, pData: HLF.Input.Draggable): void {
		if (pData instanceof DressupClothing) {
			// reset hanger scale
			pData.visuals.scale.set(1);

			// hide hint on item
			this.hideHint(pData);
			// show hint on holly
			this.showHint({x: 400, y: 300}, "hand", 5);

			if (pData === this.skirt) {
				// leave the empty hanger where it is
				changeParent(this.skirtHangerEmpty, this);
			}
			else if(pData === this.jacket) {
				// show the empty hanger
				this.jacketHanger.visible = false;
				this.jacketHangerEmpty.visible = true;
			}
			else if (pData === this.necklace) {
				// hide the patch
				this.necklacePoleCover.visible = false;
			}

			// bring to front
			HLF.PixiUtils.sendToFront(pData);
		}

	}
}